/**
 * useChatScroll - 聊天滚动逻辑 Hook
 * 处理消息列表的滚动行为
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Message } from '@/types';

interface UseChatScrollOptions {
  messages?: Message[];
}

export function useChatScroll({ messages }: UseChatScrollOptions = {}) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  // 🔥 使用 ref 追踪当前按钮状态，避免不必要的 setState 触发 re-render
  const showScrollButtonRef = useRef(false);

  /**
   * 滚动到底部
   */
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  /**
   * 立即滚动到底部（无动画）
   */
  const scrollToBottomInstant = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, []);

  /**
   * 检查是否接近底部
   */
  const isNearBottom = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return true;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    return scrollHeight - scrollTop - clientHeight < 100;
  }, []);

  // 🔥 使用 messages?.length 作为稳定依赖，避免数组引用变化导致 effect 重复执行
  const messagesLength = messages?.length ?? 0;

  // 监听滚动，显示/隐藏滚动到底部按钮
  // 🔥 修复：RAF 节流 + 滞回阈值 + 稳定依赖，
  // 彻底阻断 "滚动→setState→re-render→MermaidDiagram 重挂→高度变化→滚动" 的死循环
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId !== null) return; // 已有待处理的 RAF，跳过
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // 🔥 滞回逻辑 (Hysteresis)：显示和隐藏使用不同阈值，
        // 防止在临界区域来回滚动时按钮状态快速切换导致 re-render 风暴
        let shouldShow: boolean;
        if (showScrollButtonRef.current) {
          // 当前已显示 → 只有非常接近底部才隐藏（<80px）
          shouldShow = distanceFromBottom > 80 && scrollHeight > clientHeight;
        } else {
          // 当前已隐藏 → 只有远离底部才显示（>150px）
          shouldShow = distanceFromBottom > 150 && scrollHeight > clientHeight;
        }

        // 只在状态真正变化时才 setState，避免不必要的 re-render
        if (shouldShow !== showScrollButtonRef.current) {
          showScrollButtonRef.current = shouldShow;
          setShowScrollButton(shouldShow);
        }
      });
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    // 初始检查
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [messagesLength]);

  return {
    messagesEndRef,
    scrollContainerRef,
    showScrollButton,
    scrollToBottom,
    scrollToBottomInstant,
    isNearBottom,
  };
}

export default useChatScroll;
